import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactPlayer from 'react-player';
import { ArrowLeft, ArrowRight, Maximize2, Bookmark, X, Settings, Share2, Heart, Info, PlayCircle, BookOpen, MessageSquare, Download, Eye, Clock, User, List, Activity, Zap, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { appConfig } from '@/config/app.config';

const Player = ReactPlayer as any;

const buildAbsoluteUrl = (urlInput: string | null | undefined) => {
  if (!urlInput) return null;

  let url = urlInput.trim();

  if (url.includes('<iframe') && url.includes('src="')) {
    const match = url.match(/src="([^"]+)"/);
    if (match && match[1]) {
      url = match[1];
    }
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    if (url.includes('youtu.be/')) {
      const match = url.match(/youtu\.be\/([^\/\s]+)/);
      if (match && match[1]) {
        const videoIdAndParams = match[1];
        if (videoIdAndParams.includes('?')) {
          const [videoId, params] = videoIdAndParams.split('?');
          return `https://www.youtube.com/watch?v=${videoId}&${params}`;
        } else {
          return `https://www.youtube.com/watch?v=${videoIdAndParams}`;
        }
      }
    }
    if (url.includes('youtube.com/embed/')) {
      const match = url.match(/youtube\.com\/embed\/([^\/\s?]+)/);
      if (match && match[1]) {
        return `https://www.youtube.com/watch?v=${match[1]}`;
      }
    }
    return url;
  }

  if (url.match(/^(www\.)?(youtube\.com|youtu\.be|vimeo\.com)/)) {
    if (url.includes('youtu.be/')) {
      const match = url.match(/youtu\.be\/([^\/\s]+)/);
      if (match && match[1]) {
        const videoIdAndParams = match[1];
        if (videoIdAndParams.includes('?')) {
          const [videoId, params] = videoIdAndParams.split('?');
          return `https://www.youtube.com/watch?v=${videoId}&${params}`;
        } else {
          return `https://www.youtube.com/watch?v=${videoIdAndParams}`;
        }
      }
    }
    return `https://${url}`;
  }

  if (url.startsWith('/uploads')) {
    return `${appConfig.apiUrl}${url}`;
  }

  if (url.startsWith('/')) {
    return `${appConfig.apiUrl}${url}`;
  }
  
  return url;
};

interface TutorialStep {
  id?: string | number;
  Id?: string | number;
  sortOrder?: number;
  SortOrder?: number;
  title?: string;
  Title?: string;
  content?: string;
  Content?: string;
  content_html?: string;
  contentHtml?: string;
  imageUrl?: string;
  ImageUrl?: string;
  videoUrl?: string;
  VideoUrl?: string;
}

const TutorialStepItem = ({ step, index, onZoomImage }: { step: TutorialStep, index: number, onZoomImage: (idx: number) => void }) => {
  const stepId = step.id || step.Id || index;
  const sortOrder = step.sortOrder || step.SortOrder || index + 1;
  const title = step.title || step.Title || '';
  const content = step.content || step.Content || step.content_html || step.contentHtml;
  const imageUrl = step.imageUrl || step.ImageUrl;
  const videoUrl = step.videoUrl || step.VideoUrl;

  const [imageError, setImageError] = useState(false);

  const absoluteImageUrl = imageUrl ? buildAbsoluteUrl(imageUrl) : null;
  const absoluteVideoUrl = videoUrl ? buildAbsoluteUrl(videoUrl) : null;

  return (
    <li
      key={stepId}
      className="mb-8 last:mb-0 border-b border-slate-200 pb-8 last:border-0"
      data-step-id={stepId}
    >
      <div className="flex items-center gap-4 mb-4">
        <span className="flex items-center justify-center w-10 h-10 bg-slate-100 text-slate-900 rounded-lg font-bold">
          {sortOrder}
        </span>
        <h3 className="text-xl font-semibold text-slate-800">{title}</h3>
      </div>
      
      {content && (
        <div
          className="prose prose-slate max-w-none text-slate-600 mb-4"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      )}
      
      {absoluteImageUrl && !imageError && (
        <img
          src={absoluteImageUrl}
          alt={title}
          className="w-full max-w-4xl max-h-[600px] object-contain rounded-xl shadow-md my-6 cursor-pointer"
          loading="lazy"
          onError={() => setImageError(true)}
          onClick={() => onZoomImage(index)}
        />
      )}

      {absoluteVideoUrl && (
          <div className="aspect-video w-full rounded-xl overflow-hidden shadow-md my-6 bg-black">
            <Player
              url={absoluteVideoUrl}
              controls
              width="100%"
              height="100%"
            />
          </div>
      )}
    </li>
  );
};

const ImageZoomModal = ({
  step,
  stepIndex,
  totalSteps,
  onClose,
  onPrev,
  onNext,
}: {
  step: TutorialStep;
  stepIndex: number;
  totalSteps: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) => {
  const title = step?.title || step?.Title || '';
  const imageUrl = step?.imageUrl || step?.ImageUrl;
  const absoluteImageUrl = imageUrl ? buildAbsoluteUrl(imageUrl) : null;

  const [zoom, setZoom] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const canPrev = stepIndex > 0;
  const canNext = stepIndex < totalSteps - 1;

  useEffect(() => {
    if (!step) return;
    setZoom(1);
    setPanPosition({ x: 0, y: 0 });
  }, [step]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && canPrev) onPrev();
      if (e.key === 'ArrowRight' && canNext) onNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canPrev, canNext, onClose, onPrev, onNext]);

  if (!step || !absoluteImageUrl) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-[10000] flex items-center justify-center p-4">
      <button 
        className="absolute left-6 text-white text-6xl font-light hover:scale-110 transition-transform disabled:opacity-20"
        onClick={onPrev}
        disabled={!canPrev}
      >
        ‹
      </button>
      
      <div className="relative max-w-[95vw] max-h-[90vh] flex flex-col items-center">
        <div ref={wrapperRef} className="relative overflow-hidden cursor-grab active:cursor-grabbing">
          <img
            ref={imageRef}
            src={absoluteImageUrl}
            alt={title}
            className="rounded-lg shadow-2xl transition-transform duration-100"
            style={{ transform: `scale(${zoom}) translate(${panPosition.x}px, ${panPosition.y}px)` }}
          />
          <button 
            className="absolute top-2 right-2 text-white text-4xl hover:scale-110"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        
        {title && (
          <div className="mt-4 bg-black/50 backdrop-blur-md px-6 py-3 rounded-full text-white font-medium border border-white/10">
            {title}
          </div>
        )}
      </div>

      <button 
        className="absolute right-6 text-white text-6xl font-light hover:scale-110 transition-transform disabled:opacity-20"
        onClick={onNext}
        disabled={!canNext}
      >
        ›
      </button>
    </div>
  );
};

// --- Player Sub-components ---

const MobileTutorialPlayer = ({ steps, currentIndex, totalSteps, onNextStep, onBackToOverview, setZoomedIndex, tutorialTitle }: {
  steps: TutorialStep[];
  currentIndex: number;
  totalSteps: number;
  onNextStep: (dir: 'next' | 'prev') => void;
  onBackToOverview: () => void;
  setZoomedIndex: (idx: number) => void;
  tutorialTitle: string;
}) => {
  const currentStep = steps[currentIndex]; 
  const hasNextStep = currentIndex < totalSteps - 1;

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden relative">
      <div className="flex-shrink-0 px-6 py-4 flex items-center justify-between border-b border-slate-100 bg-white/80 backdrop-blur-xl z-30 sticky top-0">
         <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
               <BookOpen size={18} />
            </div>
            <div>
               <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest leading-none mb-1">Lukos Training</p>
               <p className="text-xs font-bold text-slate-400 truncate max-w-[150px] leading-none uppercase tracking-tighter">
                  {tutorialTitle}
               </p>
            </div>
         </div>
         <button onClick={onBackToOverview} className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 active:scale-90 transition-transform">
            <X size={20} />
         </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden no-scrollbar pb-32">
        <div className="sticky top-0 z-20 bg-black aspect-video shadow-2xl overflow-hidden group">
           <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="w-full h-full"
              >
                {currentStep?.imageUrl ? (
                  <img
                    src={buildAbsoluteUrl(currentStep.imageUrl || currentStep.ImageUrl) || ''}
                    alt="Step"
                    className="w-full h-full object-cover"
                    onClick={() => setZoomedIndex(currentIndex)}
                  />
                ) : currentStep?.videoUrl ? (
                  <Player
                    url={buildAbsoluteUrl(currentStep.videoUrl || currentStep.VideoUrl) || ''}
                    controls
                    width="100%"
                    height="100%"
                  />
                ) : (
                   <div className="w-full h-full flex items-center justify-center bg-slate-900 text-white/10">
                      <Maximize2 size={64} strokeWidth={1} />
                   </div>
                )}
              </motion.div>
           </AnimatePresence>

           <div className="absolute top-0 left-0 right-0 h-1 flex gap-0.5 px-0.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                 <div key={i} className={`h-full flex-1 transition-all duration-500 ${i <= currentIndex ? 'bg-indigo-500' : 'bg-white/10'}`} />
              ))}
           </div>

           <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end pointer-events-none">
              <div className="bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/10 text-white text-[10px] font-black uppercase tracking-widest">
                 Capítulo {currentIndex + 1}
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); setZoomedIndex(currentIndex); }}
                className="pointer-events-auto w-10 h-10 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center text-white border border-white/20 active:scale-95 transition-transform"
              >
                 <Maximize2 size={16} />
              </button>
           </div>
        </div>

        <div className="p-5 md:p-8 space-y-6 md:space-y-8 min-h-[calc(100vh-200px)]">
           <div className="space-y-4">
              <div className="flex items-center gap-3">
                 <div className="flex items-center gap-1.5 bg-indigo-50 px-3 py-1.5 rounded-full border border-indigo-100/50">
                    <Clock size={12} className="text-indigo-600" />
                    <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">5m</span>
                 </div>
                 <div className="flex items-center gap-1.5 bg-green-50 px-3 py-1.5 rounded-full border border-green-100/50">
                    <Activity size={12} className="text-green-600" />
                    <span className="text-[10px] font-black text-green-600 uppercase tracking-widest">Iniciante</span>
                 </div>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight tracking-tighter">
                 {currentStep?.title || currentStep?.Title || `Passo ${currentIndex + 1}`}
              </h2>
           </div>

           <div className="h-px w-full bg-slate-100" />

           <div 
             className="prose prose-slate max-w-none text-slate-600 leading-relaxed font-medium text-base md:text-lg pb-10"
             dangerouslySetInnerHTML={{ __html: currentStep?.content || currentStep?.content_html || '' }}
           />
        </div>
      </div>

      <div className="fixed bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-[400px]">
         <div className="bg-slate-900/95 backdrop-blur-2xl p-2 rounded-[1.5rem] md:rounded-[2rem] border border-white/10 shadow-2xl flex items-center justify-between gap-2 overflow-hidden">
            <button
               className="w-14 h-14 md:w-16 md:h-16 rounded-[1.25rem] md:rounded-[1.5rem] bg-white/5 text-white flex items-center justify-center active:bg-white/10 transition-colors disabled:opacity-20 flex-shrink-0"
               onClick={() => onNextStep('prev')}
               disabled={currentIndex === 0}
            >
               <ArrowLeft size={20} className="md:w-6 md:h-6" />
            </button>

            <button
               className="flex-1 h-14 md:h-16 rounded-[1.25rem] md:rounded-[1.5rem] bg-indigo-600 text-white flex items-center justify-center gap-2 text-xs md:text-sm font-black tracking-widest uppercase active:scale-[0.98] transition-all shadow-lg shadow-indigo-600/20"
               onClick={() => hasNextStep ? onNextStep('next') : onBackToOverview()}
            >
               <span>{hasNextStep ? 'Próximo Passo' : 'Concluir'}</span>
               <ArrowRight size={20} />
            </button>
         </div>
      </div>
    </div>
  );
};

const DesktopTutorialPlayer = ({ 
  steps, 
  tutorialTitle,
  currentIndex, 
  totalSteps, 
  onNextStep, 
  onBackToOverview, 
  setZoomedIndex,
  activeStepId,
  onStepClick
}: {
  steps: TutorialStep[];
  tutorialTitle: string;
  currentIndex: number;
  totalSteps: number;
  onNextStep: (dir: 'next' | 'prev') => void;
  onBackToOverview: () => void;
  setZoomedIndex: (idx: number) => void;
  activeStepId?: string;
  onStepClick?: (id: string) => void;
}) => {
  const currentStep = steps[currentIndex] || steps[0]; 
  const hasNextStep = currentIndex < totalSteps - 1;
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  return (
    <div className="w-full h-full bg-white flex overflow-hidden font-sans">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative w-full h-full flex"
      >
        {/* --- SIDEBAR: Khan Academy Style --- */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div 
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 340, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              className="bg-white border-r border-slate-200 flex flex-col flex-shrink-0 z-20 overflow-hidden relative shadow-sm"
            >
              <div className="p-8 border-b border-slate-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-10 h-10 bg-[#690093] rounded-xl flex items-center justify-center text-white shadow-lg shadow-purple-200">
                    <BookOpen size={20} />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-black text-base tracking-tight uppercase leading-none mb-1">Biblioteca Lukos</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Treinamento Especializado</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Progresso da Implementação</span>
                    <span className="text-[#690093] font-black text-xs">{Math.round(((currentIndex + 1) / totalSteps) * 100)}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentIndex + 1) / totalSteps) * 100}%` }}
                      className="h-full bg-[#690093] rounded-full"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-2 py-8 custom-scrollbar">
                <div className="px-4 mb-6">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Unidade 1: Fundamentos</p>
                   <p className="text-sm font-bold text-slate-800 leading-snug line-clamp-2">{tutorialTitle}</p>
                </div>

                {steps.map((step, index) => {
                  const stepId = String(step.id || step.Id);
                  const isActive = activeStepId === stepId || currentIndex === index;
                  const isCompleted = index < currentIndex;
                  
                  return (
                    <button
                      key={stepId}
                      onClick={() => onStepClick?.(stepId)}
                      className={`
                        w-full group p-4 rounded-2xl flex items-start gap-4 transition-all duration-300 text-left relative mb-2
                        ${isActive 
                          ? 'bg-purple-50 text-[#690093] shadow-sm border border-purple-100' 
                          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800 border border-transparent'
                        }
                      `}
                    >
                      <div className={`
                        w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border
                        ${isActive 
                          ? 'bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100' 
                          : isCompleted
                            ? 'bg-green-100 border-green-200 text-green-600'
                            : 'bg-white border-slate-200 text-slate-400'
                        }
                      `}>
                        {isCompleted ? <span className="text-[10px] font-black italic">✓</span> : <span className="text-[10px] font-bold">{index + 1}</span>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`
                          text-[13px] font-bold leading-snug transition-colors
                          ${isActive ? 'text-[#690093]' : isCompleted ? 'text-slate-400' : 'text-slate-700 group-hover:text-slate-900'}
                        `}>
                          {step.title || step.Title || `Passo ${index + 1}`}
                        </p>
                        {isActive && (
                           <div className="flex items-center gap-1.5 mt-2.5">
                              <div className="w-1.5 h-1.5 bg-[#690093] rounded-full animate-pulse" />
                              <span className="text-[9px] font-black uppercase tracking-widest text-[#690093]">Lendo agora</span>
                           </div>
                        )}
                      </div>
                      {isActive && <ArrowRight size={14} className="mt-1 text-[#690093]" />}
                    </button>
                  );
                })}
              </div>

              {/* Branding Footer inside Sidebar */}
              <div className="p-8 border-t border-slate-100 bg-slate-50/50">
                 <div className="flex items-center gap-2 opacity-30 grayscale">
                    <div className="w-6 h-6 bg-slate-400 rounded-md" />
                    <span className="text-[10px] font-black tracking-widest uppercase">© 2026 Lukos Soluções</span>
                 </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* --- MAIN CONTENT AREA --- */}
        <div className="flex-1 relative bg-[#f9fafb] overflow-hidden flex flex-col">
          {/* Top Navigation Bar / Breadcrumbs */}
          <div className="px-10 py-6 flex items-center justify-between bg-white border-b border-slate-200 z-30">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
              >
                <List size={20} className={isSidebarOpen ? 'rotate-0' : 'rotate-180'} />
              </button>
              
              <div className="flex items-center gap-3 text-slate-400 font-bold text-[10px] uppercase tracking-widest">
                <span className="hover:text-slate-600 cursor-pointer">Sistemas</span>
                <span className="text-slate-300">/</span>
                <span className="hover:text-slate-600 cursor-pointer">{tutorialTitle}</span>
                <span className="text-slate-300">/</span>
                <span className="text-indigo-600 font-black">{currentStep?.title || currentStep?.Title || `Passo ${currentIndex + 1}`}</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
               <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50 transition-all">
                  <Share2 size={14} /> Compartilhar Link
               </button>
               <button 
                 onClick={onBackToOverview}
                 className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-white hover:bg-slate-800 transition-colors"
               >
                  <X size={18} />
               </button>
            </div>
          </div>

          <div className="flex-1 relative flex flex-col overflow-y-auto scroll-smooth custom-scrollbar bg-slate-50">
            {/* Ambient Background */}
            <div className="absolute inset-0 -z-10 opacity-[0.03] pointer-events-none">
               <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-indigo-600 rounded-full blur-[150px]" />
               <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-600 rounded-full blur-[120px]" />
            </div>
            <div className="max-w-6xl mx-auto w-full px-10 py-12">
              
              {/* Educational Header */}
              <div className="mb-12 text-center max-w-3xl mx-auto">
                 <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-4">
                    {currentStep?.title || currentStep?.Title || `Passo ${currentIndex + 1}`}
                 </h1>
                 <div className="flex items-center justify-center gap-4">
                    <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full border border-slate-200">
                       <Clock size={12} className="text-slate-500" />
                       <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Duração: 5 min</span>
                    </div>
                    <div className="flex items-center gap-2 bg-indigo-50 px-3 py-1.5 rounded-full border border-indigo-100">
                       <Zap size={12} className="text-indigo-600" />
                       <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Interativo</span>
                    </div>
                 </div>
              </div>

              {/* Main Media Section */}
              <div className="relative w-full aspect-video rounded-[2.5rem] overflow-hidden shadow-[0_40px_100px_-20px_rgba(0,0,0,0.15)] bg-slate-900 border-[10px] border-white group mb-16">
                 <AnimatePresence mode="wait">
                    <motion.div
                      key={currentIndex}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
                      className="w-full h-full"
                    >
                       {currentStep?.imageUrl ? (
                         <img
                           src={buildAbsoluteUrl(currentStep.imageUrl || currentStep.ImageUrl) || ''}
                           alt="Step"
                           className="w-full h-full object-contain bg-slate-900"
                           onClick={() => setZoomedIndex(currentIndex)}
                         />
                       ) : currentStep?.videoUrl ? (
                         <Player
                           url={buildAbsoluteUrl(currentStep.videoUrl || currentStep.VideoUrl) || ''}
                           controls
                           width="100%"
                           height="100%"
                         />
                       ) : (
                         <div className="flex flex-col items-center justify-center text-white/5 gap-8 w-full h-full">
                           <Maximize2 size={120} strokeWidth={1} />
                         </div>
                       )}
                    </motion.div>
                 </AnimatePresence>

                 {/* Play Overlay for Image */}
                 {currentStep?.imageUrl && (
                    <div 
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 backdrop-blur-[2px] cursor-pointer" 
                      onClick={() => setZoomedIndex(currentIndex)}
                    >
                       <div className="w-24 h-24 bg-white/20 backdrop-blur-3xl rounded-full flex items-center justify-center text-white border border-white/30 shadow-2xl scale-90 group-hover:scale-100 transition-all duration-700">
                          <Maximize2 size={40} />
                       </div>
                    </div>
                 )}
              </div>

              {/* Content / Instructions Card */}
              <div className="bg-white rounded-[2.5rem] p-12 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.08)] border border-slate-200/60 mb-20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 opacity-50" />
                
                <div className="relative z-10">
                   <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-200">
                         <BookOpen size={24} />
                      </div>
                      <div>
                         <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1">INSTRUÇÕES DE EXECUÇÃO</span>
                         <h3 className="text-2xl font-black text-slate-900">Como proceder nesta etapa</h3>
                      </div>
                   </div>

                   <div 
                      className="prose prose-slate prose-lg max-w-none text-slate-600 leading-relaxed font-medium"
                      dangerouslySetInnerHTML={{ __html: currentStep?.content || currentStep?.content_html || 'Nenhuma instrução adicional disponível para este passo.' }}
                   />

                   {/* Quick Tips */}
                   <div className="mt-12 p-6 bg-slate-50 rounded-[2rem] border border-slate-100 flex items-start gap-5">
                      <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-amber-500 shrink-0">
                         <Star size={20} />
                      </div>
                      <div>
                         <p className="text-slate-900 font-bold text-sm mb-1">Dica Pro Lukos</p>
                         <p className="text-slate-500 text-xs leading-relaxed">Você pode usar os atalhos do teclado (← e →) para navegar entre os capítulos rapidamente. Mantenha o foco nas cores de destaque para identificar botões críticos.</p>
                      </div>
                   </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- FIXED FOOTER NAVIGATION: Khan Academy Style --- */}
          <div className="sticky bottom-0 px-10 py-5 bg-white/90 backdrop-blur-xl border-t border-slate-200 flex items-center justify-between z-50 shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.05)]">
             <div className="flex items-center gap-4">
                <button
                  onClick={() => currentIndex === 0 ? onBackToOverview() : onNextStep('prev')}
                  disabled={currentIndex === -1}
                  className="h-11 px-6 rounded-xl bg-slate-50 text-slate-600 font-black text-[10px] uppercase tracking-widest border border-slate-200 hover:bg-slate-100 transition-all flex items-center gap-2 disabled:opacity-30"
                >
                   <ArrowLeft size={14} /> Voltar
                </button>
             </div>

             <div className="flex items-center gap-8">
                <div className="hidden xl:flex items-center gap-3">
                   <div className="text-right">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] leading-none mb-1">Próximo</p>
                      <p className="text-xs font-bold text-slate-900 leading-none">
                         {hasNextStep ? steps[currentIndex + 1]?.title || steps[currentIndex + 1]?.Title : 'Concluir Implementação'}
                      </p>
                   </div>
                   <div className="w-9 h-9 bg-slate-50 rounded-lg flex items-center justify-center text-slate-300">
                      <ArrowRight size={18} />
                   </div>
                </div>

                <button
                  onClick={() => hasNextStep ? onNextStep('next') : onBackToOverview()}
                  className={`
                    h-11 px-10 rounded-xl flex items-center justify-center gap-3 text-[10px] font-black tracking-widest uppercase transition-all active:scale-[0.98] shadow-lg
                    ${hasNextStep 
                      ? 'bg-[#690093] text-white shadow-purple-900/20 hover:bg-[#520073]' 
                      : 'bg-green-600 text-white shadow-green-600/20 hover:bg-green-700'}
                  `}
                >
                   <span>{hasNextStep ? 'Próximo Capítulo' : 'Finalizar e Concluir'}</span>
                   <ArrowRight size={16} />
                </button>
             </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const TutorialSteps = ({
  steps,
  tutorialTitle = 'Tutorial da retaguarda',
  isPlayerMode = false,
  onNextStep,
  totalSteps,
  currentIndex,
  onBackToOverview,
  activeStepId,
  onStepClick
}: {
  steps: TutorialStep[];
  tutorialTitle?: string;
  isPlayerMode?: boolean;
  onNextStep: (dir: 'next' | 'prev') => void;
  totalSteps: number;
  currentIndex: number;
  onBackToOverview: () => void;
  activeStepId?: string;
  onStepClick?: (id: string) => void;
}) => {
  const [zoomedIndex, setZoomedIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!steps || steps.length === 0) return null;

  const currentStep = useMemo(
    () => (zoomedIndex !== null ? steps[zoomedIndex] : null),
    [steps, zoomedIndex]
  );

  const handleCloseZoom = () => setZoomedIndex(null);
  const handlePrev = () => setZoomedIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
  const handleNext = () => setZoomedIndex((prev) => (prev !== null && prev < steps.length - 1 ? prev + 1 : prev));

  if (!isPlayerMode) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-black text-slate-900 mb-12 flex items-center gap-4">
          <div className="w-1.5 h-10 bg-indigo-600 rounded-full" />
          Passo a Passo
        </h2>
        <ol className="flex flex-col gap-0">
          {steps.map((step, index) => (
            <TutorialStepItem
              key={step.id || step.Id || index}
              step={step}
              index={index}
              onZoomImage={setZoomedIndex}
            />
          ))}
        </ol>

        {zoomedIndex !== null && (
          <ImageZoomModal
            step={currentStep as any}
            stepIndex={zoomedIndex}
            totalSteps={steps.length}
            onClose={handleCloseZoom}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        )}
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      {isMobile ? (
        <MobileTutorialPlayer 
          steps={steps}
          currentIndex={currentIndex}
          totalSteps={totalSteps}
          onNextStep={onNextStep}
          onBackToOverview={onBackToOverview}
          setZoomedIndex={setZoomedIndex}
          tutorialTitle={tutorialTitle}
        />
      ) : (
        <DesktopTutorialPlayer 
          steps={steps}
          tutorialTitle={tutorialTitle}
          currentIndex={currentIndex}
          totalSteps={totalSteps}
          onNextStep={onNextStep}
          onBackToOverview={onBackToOverview}
          setZoomedIndex={setZoomedIndex}
          activeStepId={activeStepId}
          onStepClick={onStepClick}
        />
      )}

      {zoomedIndex !== null && steps[zoomedIndex] && (
        <ImageZoomModal
          step={steps[zoomedIndex] as any}
          stepIndex={zoomedIndex}
          totalSteps={totalSteps}
          onClose={handleCloseZoom}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}
    </div>
  );
};

export default TutorialSteps;
